import React from 'react'
import { startOfDay } from 'date-fns'
import { RouteComponentProps } from 'react-router'
import Card from 'components/Card'
import { CompanyPageLayout } from 'client-portal/components/PageLayout'
import { Box, Container } from 'components/Layout'
import {
  useStateWithDebounce,
  getFormattedDateText,
  useClearanceStatus,
  useAuth,
  GroupMemberRole,
  useGroupsData,
  useAddClearanceStatus,
  useApproveClearanceStatus,
  useDenyClearanceStatus,
  Me_me_User_GroupsWithRole,
  Groups_groups_Groups,
  useRunGroupClearance,
  useApproveGroupClearanceStatus,
  useDenyGroupClearanceStatus,
  ClearanceStatusType,
  useResendEmailQuestionnaire,
  CompanyMemberRole,
} from '@modmd/data'
import DropdownButton from 'components/DropdownButton'
import { useToaster } from 'utils/useToaster'
import { DateInputButton } from 'components/DateInputButton'
import { CompanyEventClearanceDatalist } from 'sharedComponents/CompanyEventClearanceDatalist'
import { useSearchState } from 'internal-portal/utils/useSearchState'
import TextInput from 'components/TextInput'
import Icon from 'components/Icon'
import Button from 'components/Button'
import { Spinner } from 'components/Spinner'
import { COLOR } from 'theme'
import { Text } from 'components/Typography'
import { DownloadClearanceButton } from './DownloadClearanceButton'

interface RouterProps {
  companyId: string
}

export const CompanyEventClearance: React.VFC<RouteComponentProps<RouterProps>> = ({ match }) => {
  const { data: authData, getUserCompaniesWithRole, getUserGroupsWithRole } = useAuth()
  const { setToastMessage } = useToaster()
  const [addClearanceStatus, { loading: isAddClearanceStatusLoading }] = useAddClearanceStatus()
  const [
    resendEmailQuestionnaire,
    { loading: isResendEmailQuestionnaireLoading },
  ] = useResendEmailQuestionnaire()
  const [runGroupClearance, { loading: isRunGroupClearanceLoading }] = useRunGroupClearance()
  const [
    approveClearanceStatus,
    { loading: isApproveClearanceStatusLoading },
  ] = useApproveClearanceStatus()
  const [denyClearanceStatus, { loading: isDenyClearanceStatusLoading }] = useDenyClearanceStatus()
  const [
    approveGroupClearanceStatus,
    { loading: isApproveGroupClearanceStatusLoading },
  ] = useApproveGroupClearanceStatus()
  const [
    denyGroupClearanceStatus,
    { loading: isDenyGroupClearanceStatusLoading },
  ] = useDenyGroupClearanceStatus()

  const { companies: companiesUserManages, hasCompaniesWithRole } = getUserCompaniesWithRole({
    roles: [CompanyMemberRole.Owner, CompanyMemberRole.Manager],
  })

  const { groups: groupsUserManages } = getUserGroupsWithRole({
    roles: [GroupMemberRole.Manager, GroupMemberRole.Coordinator],
  })

  const { data: companyGroups, loading: isGroupsLoading } = useGroupsData({
    filter: {
      companyIds: hasCompaniesWithRole ? companiesUserManages.map(({ id }) => id) : [''],
    },
  })

  const companyGroupsFormated = (companyGroups?.groups?.Groups.map(
    (group: Groups_groups_Groups) => ({
      id: group.id,
      companyId: group?.Company?.id,
      name: group?.name,
      role: group?.Members?.Members.length
        ? group?.Members?.Members?.[0].role
        : GroupMemberRole.Member,
    })
  ) as unknown) as Me_me_User_GroupsWithRole

  const groupsSelect =
    hasCompaniesWithRole && companyGroups?.groups?.Groups.length
      ? groupsUserManages.concat(companyGroupsFormated)
      : groupsUserManages

  const [selectedMembers, setSelectedMembers] = React.useState<
    {
      userId: string
      clearanceId: string | null
      clearanceCheckStatus: ClearanceStatusType
      entranceStatus: ClearanceStatusType
    }[]
  >([])

  const searchState = useSearchState<{
    createdAt: string
    nameOrClearanceIdLike: string
    page?: string
  }>()

  const [createdAtLike, setcreatedAtLike] = useStateWithDebounce(
    searchState.data.createdAt || startOfDay(new Date()).toISOString(),
    (createdAtLike) => {
      searchState.setSearchState({ createdAt: createdAtLike, page: 1 }, false, true)
    }
  )

  const [group, setGroup] = React.useState(companyGroups?.groups?.Groups[0] ?? groupsUserManages[0])

  const {
    data: clearanceStatus,
    loading: isClearanceStatusLoading,
    refetch: refetchClearanceStatus,
  } = useClearanceStatus({
    filter: Object.entries({
      createdAt: searchState.data.createdAt,
      groupIds: [group ? group.id : ''],
      nameOrClearanceIdLike: searchState.data.nameOrClearanceIdLike,
    }).reduce((acc, [key, value]) => (value ? { ...acc, [key]: value } : acc), {
      groupIds: [group ? group.id : ''],
    }),
    page: Number(searchState.data.page),
  })

  const handleRunGroupClearance = async () => {
    try {
      await runGroupClearance({
        variables: {
          inputData: {
            groupId: group.id,
            createdAt: searchState.data.createdAt,
            usersIds: selectedMembers.length ? selectedMembers.map(({ userId }) => userId) : null,
          },
        },
      })
      setSelectedMembers([])
      setToastMessage('Group Clearance created', 'success')
      await refetchClearanceStatus()
    } catch {
      // ignore error
    }
  }

  const handleResendEmailQuestionnaire = async (userId: string, groupId: string) => {
    try {
      await resendEmailQuestionnaire({
        variables: {
          inputData: {
            userId,
            groupId,
            createdAt: searchState.data.createdAt,
          },
        },
      })
      setToastMessage('Email sent', 'success')
    } catch {
      // ignore error
    }
  }

  const handleRunClearance = async (userId: string, groupId: string) => {
    try {
      await addClearanceStatus({
        variables: {
          inputData: {
            userId,
            groupId,
            createdAt: searchState.data.createdAt,
          },
        },
      })
      setSelectedMembers([])
      setToastMessage('Clearance Status created', 'success')
      await refetchClearanceStatus()
    } catch {
      // ignore error
    }
  }

  const handleDenyClearance = async (clearanceId: string, groupId: string) => {
    try {
      await denyClearanceStatus({
        variables: {
          inputData: {
            clearanceId,
            entranceResponsible: authData.User.id,
            groupId,
          },
        },
      })
      setSelectedMembers([])
      setToastMessage('Entrance denied', 'success')
      await refetchClearanceStatus()
    } catch {
      // ignore error
    }
  }

  const handleApproveClearance = async (clearanceId: string, groupId: string) => {
    try {
      await approveClearanceStatus({
        variables: {
          inputData: {
            clearanceId,
            entranceResponsible: authData.User.id,
            groupId,
          },
        },
      })
      setSelectedMembers([])
      setToastMessage('Entrance approved', 'success')
      await refetchClearanceStatus()
    } catch {
      // ignore error
    }
  }

  const handleChangeSelectedMembers = (
    userId: string,
    clearanceId: string | null,
    clearanceCheckStatus: ClearanceStatusType,
    entranceStatus: ClearanceStatusType
  ) => {
    if (
      selectedMembers.some(
        (member) => member.userId === userId && member.clearanceId === clearanceId
      )
    )
      setSelectedMembers(selectedMembers.filter((item) => item.userId !== userId))
    else
      setSelectedMembers([
        ...selectedMembers,
        { userId, clearanceId, clearanceCheckStatus, entranceStatus },
      ])
  }

  const handleApproveGroupClearance = async () => {
    try {
      await approveGroupClearanceStatus({
        variables: {
          inputData: {
            clearancesIds: selectedMembers.map(({ clearanceId }) => clearanceId),
            entranceResponsible: authData.User.id,
            groupId: group.id,
          },
        },
      })
      setSelectedMembers([])
      setToastMessage('Entrance approved', 'success')
      await refetchClearanceStatus()
    } catch {
      // ignore error
    }
  }

  const handleDenyGroupClearance = async () => {
    try {
      await denyGroupClearanceStatus({
        variables: {
          inputData: {
            clearancesIds: selectedMembers
              .filter(({ clearanceId }) => !!clearanceId)
              .map(({ clearanceId }) => clearanceId) as string[],
            entranceResponsible: authData.User.id,
            groupId: group.id,
          },
        },
      })
      setSelectedMembers([])
      setToastMessage('Entrance denied', 'success')
      await refetchClearanceStatus()
    } catch {
      // ignore error
    }
  }

  return (
    <CompanyPageLayout>
      <Card>
        <Box>
          <Card.Title
            rightContent={
              <>
                <p>
                  <DateInputButton
                    id="dateTo"
                    name="dateTo"
                    label={getFormattedDateText(createdAtLike)}
                    value={new Date(createdAtLike)}
                    onChange={(date) => {
                      setcreatedAtLike(startOfDay(new Date(date)).toISOString())
                      setSelectedMembers([])
                    }}
                    hasBottomContent={false}
                    isFullWidth
                  />
                </p>
                <DownloadClearanceButton searchState={searchState} group={group} />
              </>
            }
          >
            FastPass
          </Card.Title>
          {!isGroupsLoading && (
            <Card.Content>
              <Container>
                <CompanyEventClearanceDatalist
                  data={clearanceStatus?.getClearanceStatus?.users || []}
                  pagination={clearanceStatus?.getClearanceStatus?.pagination}
                  isLoading={isClearanceStatusLoading || isRunGroupClearanceLoading}
                  requestDebounceInMs={250}
                  tableHeader={
                    <Box gridGap="1rem" alignItems="center">
                      <Box
                        display="flex"
                        gridGap="1rem"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Box zIndex={1}>
                          <DropdownButton
                            buttonColorVariant="primary"
                            appearance="link"
                            buttonName={`Group: ${group?.name ?? 'Select a group'}`}
                          >
                            {groupsSelect.map((groupList: Me_me_User_GroupsWithRole) => (
                              <DropdownButton.Item
                                appearance="link"
                                onClick={() => {
                                  setGroup(groupList)
                                  setSelectedMembers([])
                                }}
                              >
                                {groupList.name}
                              </DropdownButton.Item>
                            ))}
                          </DropdownButton>
                        </Box>
                        <Box ml="1rem" display="flex">
                          <Button
                            onClick={handleRunGroupClearance}
                            appearance="link"
                            size="large"
                            disabled={isRunGroupClearanceLoading}
                          >
                            {isRunGroupClearanceLoading ? (
                              <Spinner size="small" color={COLOR.brand} />
                            ) : (
                              <Text color="primary" fontSize="m">
                                {selectedMembers.length
                                  ? 'Run Selected Members FastPass'
                                  : 'Run Group FastPass'}
                              </Text>
                            )}
                          </Button>
                          {selectedMembers.length &&
                          selectedMembers.every(
                            (member) =>
                              !!member.clearanceId &&
                              member.clearanceCheckStatus === ClearanceStatusType.NotSubmitted &&
                              member.entranceStatus === ClearanceStatusType.NotSubmitted
                          ) ? (
                            <Box ml="1.5rem">
                              <Button
                                onClick={handleApproveGroupClearance}
                                appearance="link"
                                size="large"
                                disabled={isApproveGroupClearanceStatusLoading}
                              >
                                {isApproveGroupClearanceStatusLoading ? (
                                  <Spinner size="small" color={COLOR.brand} />
                                ) : (
                                  <Icon.Checkmark size="XL" color={COLOR.green} />
                                )}
                              </Button>
                              <Button
                                onClick={handleDenyGroupClearance}
                                appearance="link"
                                size="large"
                                disabled={isDenyGroupClearanceStatusLoading}
                              >
                                {isDenyGroupClearanceStatusLoading ? (
                                  <Spinner size="small" color={COLOR.brand} />
                                ) : (
                                  <Icon.Close size="XL" color={COLOR.danger} />
                                )}
                              </Button>
                            </Box>
                          ) : null}
                        </Box>
                      </Box>
                      <Box mb="1rem">
                        <TextInput
                          name="nameOrClearanceIdLike"
                          value={searchState.data.nameOrClearanceIdLike || ''}
                          onChange={(e) => {
                            searchState.setSearchState({
                              ...searchState.data,
                              nameOrClearanceIdLike: e.target.value,
                            })
                          }}
                          leftIcon={<Icon.Search />}
                          placeholder="Search for name or Clearance Id"
                          hasBottomContent={false}
                          isFullWidth
                        />
                      </Box>
                    </Box>
                  }
                  onPageChange={(pageNumber) => {
                    searchState.setSearchState({ page: pageNumber }, false)
                  }}
                  pageLength={clearanceStatus?.getClearanceStatus?.pagination.length}
                  handleRunClearance={handleRunClearance}
                  isAddClearanceStatusLoading={
                    isAddClearanceStatusLoading || isRunGroupClearanceLoading
                  }
                  handleResendEmailQuestionnaire={handleResendEmailQuestionnaire}
                  isResendEmailQuestionnaireLoading={isResendEmailQuestionnaireLoading}
                  handleDenyClearance={handleDenyClearance}
                  isDenyClearanceStatusLoading={isDenyClearanceStatusLoading}
                  handleApproveClearance={handleApproveClearance}
                  isApproveClearanceStatusLoading={isApproveClearanceStatusLoading}
                  companyId={match.params.companyId}
                  handleChangeSelectedMembers={handleChangeSelectedMembers}
                  selectedMembers={selectedMembers}
                />
              </Container>
            </Card.Content>
          )}
        </Box>
      </Card>
    </CompanyPageLayout>
  )
}
